import { useState } from 'react';
import * as Form from 'components/form';
import {
    SearchableSelectProps,
    SelectItemWithLabel,
    SelectListItem,
    UseFormSearchableSelect,
} from 'components/select';
import { ContactType } from 'constants/contacts';
import useDebounce from 'customHooks/useDebounce';
import { useInfiniteContacts } from 'customHooks/useInfiniteContacts';
import { Contact } from 'types/domain';

interface SelectContactFormValues {
    selectedContact: SelectItemWithLabel<Contact>[];
}

interface SelectContactFormProps
    extends Pick<SearchableSelectProps<Contact, SelectItemWithLabel<Contact>>, 'onSelectItem'> {}

const SelectContactForm = (props: SelectContactFormProps) => {
    const { onSelectItem } = props;
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search);

    const { data, ref } = useInfiniteContacts(
        {
            query: debouncedSearch,
            type: ContactType.Individual,
            nameRequired: true,
        },
        {
            staleTime: Infinity,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
        },
    );
    const contacts = data?.pages ?? [];
    const { control, path } = Form.useFormContext<SelectContactFormValues>();

    return (
        <>
            <UseFormSearchableSelect
                control={control}
                name={'selectedContact'}
                localSearch={false}
                clearable={true}
                inputProps={{
                    placeholder: 'Search contacts',
                    onChange: e => {
                        setSearch(e.currentTarget.value);
                    },
                }}
                onSelectItem={(value, ...rest) => {
                    if (!value) {
                        setSearch('');
                        return;
                    }

                    onSelectItem?.(value, ...rest);
                }}
            >
                {contacts.map((item: Contact) => (
                    <SelectListItem
                        key={item.id}
                        value={{
                            id: item.id.toString(),
                            label: `${item.first_name} ${item.last_name}${
                                item.primary_email ? ` - ${item.primary_email}` : ''
                            }`,
                            value: item,
                        }}
                    >
                        <div className={'flex flex-col gap-2'}>
                            <p className="font-medium">{`${item.first_name} ${item.last_name}`}</p>
                            {!!item.primary_email && (
                                <p className="font-thin text-gray-600">{`${item.primary_email}`}</p>
                            )}
                        </div>
                    </SelectListItem>
                ))}
                <div ref={ref} />
            </UseFormSearchableSelect>

            <Form.Error name={path('selectedContact')} />
        </>
    );
};

export { SelectContactForm };
export type { SelectContactFormValues };
